/**
 * Per-pillar markdown section builders for the maintenance health document.
 *
 * Each function turns a single PillarScore into a markdown section. The
 * main document orchestrator in `generateDriftDocument.ts` composes these
 * sections via `generatePillarSection`.
 */

import type {
  DriftReport,
  PillarScore,
  ActivityPillarDetails,
  CICDPillarDetails,
  IssuesPRsPillarDetails,
  DependencyPillarDetails,
  ReleasePillarDetails,
  MetadataPillarDetails,
} from '../../types/analysis.types';

export const gradeDescriptions: Record<string, string> = {
  A: 'Excellent - Well maintained repository',
  B: 'Good - Minor improvements needed',
  C: 'Fair - Attention needed soon',
  D: 'Poor - Significant maintenance required',
  F: 'Critical - Major technical debt',
};

export function getPillarGrade(score: number): string {
  if (score >= 85) return 'A';
  if (score >= 70) return 'B';
  if (score >= 55) return 'C';
  if (score >= 40) return 'D';
  return 'F';
}

export function formatPillarName(name: string): string {
  const nameMap: Record<string, string> = {
    activity: 'Activity',
    cicd: 'CI/CD',
    issues_prs: 'Issues & PRs',
    dependency: 'Dependency',
    release: 'Release',
    metadata: 'Metadata',
  };
  return nameMap[name] || name;
}

/** Format weight as percentage (input is 0-1 decimal or 0-100) */
export function formatWeight(weight: number): string {
  // If weight is already > 1, assume it's a percentage
  const pct = weight > 1 ? weight : weight * 100;
  return `${Math.round(pct)}%`;
}

function generateActivitySection(pillar: PillarScore): string {
  const details = pillar.details as ActivityPillarDetails;
  const trend =
    details.commitTrend === 'increasing'
      ? '↑ Increasing'
      : details.commitTrend === 'declining'
        ? '↓ Declining'
        : '→ Stable';

  return `### Activity (${formatWeight(pillar.weight)} weight)
**Score: ${pillar.score}/100 (${getPillarGrade(pillar.score)})**

Measures commit frequency, recency, and contributor engagement.

| Metric | Value |
|--------|-------|
| Commits (30d) | ${details.commitCount30d} |
| Commits (90d) | ${details.commitCount90d} |
| Days Since Last Commit | ${details.daysSinceLastCommit} |
| Active Contributors (30d) | ${details.activeContributors30d} |
| Commit Trend | ${trend} |
`;
}

function generateCICDSection(pillar: PillarScore): string {
  const details = pillar.details as CICDPillarDetails;
  const capabilities = [
    details.hasCI ? '✅ CI Pipeline' : '❌ CI Pipeline',
    details.hasCD ? '✅ CD Pipeline' : '❌ CD Pipeline',
    details.hasTestAutomation ? '✅ Test Automation' : '❌ Test Automation',
  ];

  return `### CI/CD (${formatWeight(pillar.weight)} weight)
**Score: ${pillar.score}/100 (${getPillarGrade(pillar.score)})**

Measures continuous integration and deployment automation.

| Metric | Value |
|--------|-------|
| CI Platform | ${details.ciPlatform || 'None detected'} |
| Workflows | ${details.workflowCount} |
| Has CI | ${details.hasCI ? 'Yes' : 'No'} |
| Has CD | ${details.hasCD ? 'Yes' : 'No'} |
| Test Automation | ${details.hasTestAutomation ? 'Yes' : 'No'} |

**Capabilities:**
${capabilities.map((c) => `- ${c}`).join('\n')}
`;
}

function generateIssuesPRsSection(pillar: PillarScore): string {
  const details = pillar.details as IssuesPRsPillarDetails;
  const avgPRTime =
    details.avgPRMergeTimeHours != null && !isNaN(details.avgPRMergeTimeHours)
      ? `${Math.round(details.avgPRMergeTimeHours)}h`
      : 'N/A';
  const avgIssueTime =
    details.avgIssueCloseTimeHours != null && !isNaN(details.avgIssueCloseTimeHours)
      ? `${Math.round(details.avgIssueCloseTimeHours)}h`
      : 'N/A';

  return `### Issues & PRs (${formatWeight(pillar.weight)} weight)
**Score: ${pillar.score}/100 (${getPillarGrade(pillar.score)})**

Measures pull request velocity and issue management.

| Metric | Value |
|--------|-------|
| Open PRs | ${details.openPRCount ?? 0} |
| Merged PRs (30d) | ${details.mergedPRCount30d ?? 0} |
| Avg PR Merge Time | ${avgPRTime} |
| Open Issues | ${details.openIssueCount ?? 0} |
| Closed Issues (30d) | ${details.closedIssueCount30d ?? 0} |
| Avg Issue Close Time | ${avgIssueTime} |

**Templates:**
- Issue Templates: ${details.hasIssueTemplates ? '✅ Yes' : '❌ No'}
- PR Templates: ${details.hasPRTemplates ? '✅ Yes' : '❌ No'}
`;
}

function generateDependencySection(pillar: PillarScore, drift?: DriftReport): string {
  const details = pillar.details as DependencyPillarDetails;
  const driftPercent = Math.round((details.driftRatio ?? 0) * 100);
  // Use drift data for oldest years if pillar details is 0 or missing
  const oldestYears =
    details.oldestPinnedYears && details.oldestPinnedYears > 0
      ? details.oldestPinnedYears
      : (drift?.metrics?.oldestPinnedYears ?? 0);

  let section = `### Dependency (${formatWeight(pillar.weight)} weight)
**Score: ${pillar.score}/100 (${getPillarGrade(pillar.score)})**

Measures package freshness and drift management.

| Metric | Value |
|--------|-------|
| Total Dependencies | ${details.totalDependencies ?? drift?.metrics?.totalDependencies ?? 0} |
| Outdated Dependencies | ${details.outdatedDependencies ?? drift?.metrics?.outdatedDependencies ?? 0} (${driftPercent}%) |
| Oldest Package | ${oldestYears.toFixed(1)} years behind |
| Dependabot | ${details.hasDependabot ? '✅ Enabled' : '❌ Not enabled'} |
| Renovate | ${details.hasRenovate ? '✅ Enabled' : '❌ Not enabled'} |
`;

  // Add stale dependencies table if drift data available
  if (drift?.staleDependencies && drift.staleDependencies.length > 0) {
    section += `
**Top Packages Needing Updates:**

| Package | Current | Latest | Age |
|---------|---------|--------|-----|
${drift.staleDependencies
  .slice(0, 8)
  .map(
    (dep) =>
      `| ${dep.name ?? 'unknown'} | ${dep.pinned ?? '-'} | ${dep.latest ?? '-'} | ${(dep.ageYears ?? 0).toFixed(1)}yr |`,
  )
  .join('\n')}
${drift.staleDependencies.length > 8 ? `\n*...and ${drift.staleDependencies.length - 8} more packages*` : ''}
`;
  }

  return section;
}

function generateReleaseSection(pillar: PillarScore): string {
  const details = pillar.details as ReleasePillarDetails;
  const daysSince =
    details.daysSinceLastRelease !== null ? `${details.daysSinceLastRelease} days` : 'No releases';
  const frequency =
    details.releaseFrequencyDays !== null ? `Every ${details.releaseFrequencyDays} days` : 'N/A';

  return `### Release (${formatWeight(pillar.weight)} weight)
**Score: ${pillar.score}/100 (${getPillarGrade(pillar.score)})**

Measures release frequency and versioning practices.

| Metric | Value |
|--------|-------|
| Releases (12m) | ${details.releaseCount12m} |
| Days Since Last Release | ${daysSince} |
| Release Frequency | ${frequency} |
| Semantic Versioning | ${details.hasSemanticVersioning ? '✅ Yes' : '❌ No'} |
| Changelog | ${details.hasChangelog ? '✅ Yes' : '❌ No'} |
`;
}

function generateMetadataSection(pillar: PillarScore): string {
  const details = pillar.details as MetadataPillarDetails;
  const checklist = [
    details.hasReadme ? '✅ README' : '❌ README',
    details.hasLicense ? '✅ License' : '❌ License',
    details.hasDescription ? '✅ Description' : '❌ Description',
    details.hasTopics ? `✅ Topics (${details.topicCount})` : '❌ Topics',
    details.hasContributing ? '✅ Contributing Guide' : '❌ Contributing Guide',
    details.hasCodeOfConduct ? '✅ Code of Conduct' : '❌ Code of Conduct',
  ];
  const complete = checklist.filter((c) => c.startsWith('✅')).length;

  return `### Metadata (${formatWeight(pillar.weight)} weight)
**Score: ${pillar.score}/100 (${getPillarGrade(pillar.score)})**

Measures documentation, licensing, and discoverability.

**Documentation Completeness: ${complete}/6 (${Math.round((complete / 6) * 100)}%)**

${checklist.map((c) => `- ${c}`).join('\n')}
`;
}

export function generatePillarSection(pillar: PillarScore, drift?: DriftReport): string {
  switch (pillar.name) {
    case 'activity':
      return generateActivitySection(pillar);
    case 'cicd':
      return generateCICDSection(pillar);
    case 'issues_prs':
      return generateIssuesPRsSection(pillar);
    case 'dependency':
      return generateDependencySection(pillar, drift);
    case 'release':
      return generateReleaseSection(pillar);
    case 'metadata':
      return generateMetadataSection(pillar);
    default:
      return '';
  }
}
